// Post-Purchase-Delivery-Anzeige (LEA-379, Om-QA 03.07.): Nach dem 49-€-Kauf
// liefert der Seed die 50 Leads über mehrere Minuten — vorher tröpfelten sie
// kommentarlos ein („läuft noch" vs. „hängt" ununterscheidbar, manueller
// Refresh nötig, sinkende Credits wirkten wie ein Bug). Dieser Banner macht
// die Wartezeit zum Produkt-Moment: Live-Fortschritt X/50, Auto-Refresh der
// Leads über den bestehenden `cortex:profiles-stale`-Bus, und die Credits-
// Mechanik wird erklärt (Model A: 1 Start-Credit je geliefertem Lead).
//
// Selbst-gated: pollt /api/prism/seed-status (read-only); kein aktiver oder
// frisch abgeschlossener Seed (bzw. Flag aus → 404) → rendert nichts und
// stoppt. Erfolgs-Zustand bleibt bis DONE_LINGER_MS nach seededAt sichtbar.

import { t, useLang } from '@cortex/i18n';
import { useEffect, useRef, useState } from 'react';

type SeedStatus = {
  active: boolean;
  status?: string;
  delivered?: number;
  total?: number;
  seededAt?: number | null;
};

const POLL_MS = 6000;
const DONE_LINGER_MS = 15 * 60 * 1000;

export default function SeedProgressBanner() {
  useLang();
  const [st, setSt] = useState<SeedStatus | null>(null);
  const lastDelivered = useRef(-1);

  useEffect(() => {
    let timer: number | undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch('/api/prism/seed-status', { credentials: 'include' });
        if (!r.ok) return; // Flag aus (404) / nicht eingeloggt → nie wieder pollen
        const data = (await r.json()) as SeedStatus;
        if (cancelled) return;
        setSt(data);
        if (typeof data.delivered === 'number' && data.delivered !== lastDelivered.current) {
          // Neue Leads angekommen → Kanban/Leads über den bestehenden
          // Refresh-Bus der App nachladen (kein manueller Refresh mehr).
          lastDelivered.current = data.delivered;
          window.dispatchEvent(new Event('cortex:profiles-stale'));
        }
        if (data.active) timer = window.setTimeout(poll, POLL_MS);
      } catch {
        // transienter Netzfehler — nächster App-Boot pollt erneut
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  if (!st) return null;
  const seeding = st.active === true;
  const freshDone =
    !seeding &&
    st.status === 'seeded' &&
    typeof st.seededAt === 'number' &&
    Date.now() - st.seededAt < DONE_LINGER_MS &&
    (st.delivered ?? 0) > 0;
  if (!seeding && !freshDone) return null;

  const delivered = st.delivered ?? 0;
  const total = st.total ?? 50;
  const pct = Math.max(4, Math.min(100, (delivered / total) * 100));

  return (
    <div
      className="relative overflow-hidden"
      style={{ background: 'linear-gradient(107deg, #0B1919 0%, #12332B 55%, #17493A 100%)' }}
    >
      <div className="max-w-[1440px] mx-auto px-6 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <span className="hidden sm:inline-flex items-center gap-1.5 h-[22px] px-2.5 rounded-full bg-white/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] text-white text-[10.5px] font-semibold uppercase tracking-[0.08em] whitespace-nowrap">
          {seeding ? (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
          ) : (
            '✓'
          )}
          {t('prism.seed.badge')}
        </span>
        <div className="text-[12.5px] text-white/90 flex-1 min-w-0">
          {seeding ? (
            <>
              <span className="font-medium text-white tabular-nums">
                {t('prism.seed.working', { delivered, total })}
              </span>
              <span className="text-white/70 ml-1.5 hidden md:inline">
                {t('prism.seed.workingSub')}
              </span>
              <span className="text-white/60 ml-1.5 hidden lg:inline">
                {t('prism.seed.creditsNote', { total })}
              </span>
            </>
          ) : (
            <span className="font-medium text-white">
              {delivered >= total
                ? t('prism.seed.done', { delivered })
                : t('prism.seed.donePartial', { delivered })}
            </span>
          )}
        </div>
        {seeding && (
          <div className="flex items-center gap-2 text-[11.5px] text-white/70">
            <span className="tabular-nums whitespace-nowrap">
              {delivered}/{total}
            </span>
            <div className="w-24 h-1 rounded-full bg-white/15 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-300 to-teal-200 transition-[width] duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
